import {
  Directive,
  ElementRef,
  Renderer2,
  input,
  computed,
  effect,
} from '@angular/core';

export type ButtonVariant =
  | 'neutral'
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'ghost'
  | 'link'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'default';
type ButtonSize = 'lg' | 'md' | 'sm' | 'xs';
type ButtonShape = 'circle' | 'square';

@Directive({
  selector: 'button[appButton], a[appButton]',
  standalone: true,
})
export class ButtonDirective {

  public variant = input<ButtonVariant>('neutral', { alias: 'appButton' });

  public size = input<ButtonSize | null>(null);

  public shape = input<ButtonShape | null>(null);

  public block = input<boolean>(false);

  public loading = input<boolean>(false);

  public disabled = input<boolean>(false);

  private _spinnerElement: HTMLElement | null = null;
  private _previousBtnClasses: string[] = [];

  private readonly isDisabled = computed(() => this.loading() || this.disabled());

  private readonly buttonClasses = computed(() => {
    const classes = ['btn'];

    const variantClassMap: Record<ButtonVariant, string> = {
      neutral: 'btn-neutral',
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      accent: 'btn-accent',
      ghost: 'btn-ghost',
      link: 'btn-link',
      info: 'btn-info',
      success: 'btn-success',
      warning: 'btn-warning',
      error: 'btn-error',
      default: '',
    };
    const isDisabled = this.isDisabled();

    if (!isDisabled) {
      const variant = this.variant();
      classes.push(variantClassMap[variant]);

    }

    const size = this.size();
    if (size) {
      const sizeClassMap: Record<ButtonSize, string> = {
        lg: 'btn-lg',
        md: 'btn-md',
        sm: 'btn-sm',
        xs: 'btn-xs',
      };
      classes.push(sizeClassMap[size]);
    }

    const shape = this.shape();
    if (shape) {
      const shapeClassMap: Record<ButtonShape, string> = {
        circle: 'btn-circle',
        square: 'btn-square',
      };
      classes.push(shapeClassMap[shape]);
    }

    if (this.block()) classes.push('btn-block');
    if (isDisabled) classes.push('btn-disabled');

    return classes.join(' ');
  });

  constructor(
    private _elementRef: ElementRef<HTMLElement>,
    private _renderer: Renderer2,
  ) {
    effect(() => {
      const newClasses = this.buttonClasses().split(' ').filter(Boolean);
      const nativeEl = this._elementRef.nativeElement;

      for (const cls of this._previousBtnClasses) {
        this._renderer.removeClass(nativeEl, cls);
      }
      for (const cls of newClasses) {
        this._renderer.addClass(nativeEl, cls);
      }
      this._previousBtnClasses = newClasses;
    });

    effect(() => {
      const nativeEl = this._elementRef.nativeElement;
      const isDisabled = this.isDisabled();
      this._renderer.setProperty(nativeEl, 'disabled', isDisabled);
      this._updateAriaDisabled(nativeEl, isDisabled);
    });

    effect(() => {
      this._updateSpinner(this.loading());
    });
  }

  private _updateSpinner(isLoading: boolean): void {
    const nativeEl = this._elementRef.nativeElement;

    if (isLoading) {
      if (!this._spinnerElement) {
        this._spinnerElement = this._renderer.createElement('span');
        this._renderer.addClass(this._spinnerElement, 'loading');
        this._renderer.addClass(this._spinnerElement, 'loading-spinner');
        this._renderer.appendChild(nativeEl, this._spinnerElement);
      }
    } else {
      if (this._spinnerElement) {
        this._renderer.removeChild(nativeEl, this._spinnerElement);
        this._spinnerElement = null;
      }
    }
  }

  private _updateAriaDisabled(
    el: HTMLElement,
    isDisabled: boolean,
  ): void {
    if (el.tagName !== 'A') return;
    if (isDisabled) {
      this._renderer.setAttribute(el, 'aria-disabled', 'true');
    } else {
      this._renderer.removeAttribute(el, 'aria-disabled');
    }
  }
}
